/**
 * @pg/variants: <pg-variant-picker> (markup: snippets/pg-variant-picker.liquid).
 *
 * On every option change it:
 * 1. resolves the variant from the picker's JSON (instant, no network);
 * 2. writes ?variant= to the URL (main product page only), then sets the hidden
 *    `[data-pg-variant-input]` id inputs and fires `change` on them. That order matters for the
 *    app embeds: Krtbite back-in-stock reads ?variant= on any `change`, EZ Pre-order listens to
 *    `change` on the first [name="id"];
 * 3. announces the choice with `pg:variant:change` (theme components: gallery, buy buttons) and
 *    Shopify's standard ProductSelectEvent (@shopify/events) for apps and agents;
 * 4. re-renders every `[data-pg-variant-part]` inside the product root ([data-pg-product]) from
 *    the Section Rendering API (price, stock, pickup, option availability), then dispatches
 *    `pg:content:rendered` so partner prices re-apply. Stale responses are dropped.
 */
import { PgElement, define } from '@pg/core';
import { fetchSection } from '@pg/section-renderer';
import { ProductSelectEvent } from '@shopify/events';

class PgVariantPicker extends PgElement {
  /** @type {{ id: number, options: string[], available: boolean, media: number|null }[]} */
  variants = [];
  #request = 0;

  mount() {
    try {
      this.variants = JSON.parse(this.querySelector('script[data-pg-variants]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }
    this.addEventListener('change', (event) => this.#onChange(event), { signal: this.signal });
  }

  /** The element that holds everything this product's variant affects. */
  get root() {
    return this.closest('[data-pg-product]') || this.parentElement || this;
  }

  /** @returns {{ name: string, value: string }[]} */
  selectedOptions() {
    return [...this.querySelectorAll('[data-pg-option]')].map((option) => {
      const select = option.querySelector('select');
      const checked = /** @type {HTMLInputElement|null} */ (option.querySelector('input:checked'));
      const legend = option.querySelector('legend, label');
      const name = (legend?.firstChild?.textContent || '').replace(/:\s*$/, '').trim();
      return { name, value: select ? select.value : checked?.value || '' };
    });
  }

  /** @param {Event} event */
  #onChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.matches?.('[data-pg-option-input]')) return;

    // Keep the "Size: M" legend in step immediately.
    const option = input.closest('[data-pg-option]');
    const label = option?.querySelector('[data-pg-option-value]');
    if (label) label.textContent = input.value;

    const selected = this.selectedOptions();
    const values = selected.map((o) => o.value);
    const variant = this.variants.find((v) => v.options.every((value, i) => value === values[i])) || null;
    this.#select(variant, selected);
  }

  /**
   * @param {{ id: number, options: string[], available: boolean, media: number|null } | null} variant
   * @param {{ name: string, value: string }[]} selectedOptions
   */
  async #select(variant, selectedOptions) {
    const root = this.root;
    const request = ++this.#request;

    if (variant && this.hasAttribute('data-update-url')) {
      const url = new URL(location.href);
      url.searchParams.set('variant', String(variant.id));
      history.replaceState(history.state, '', url.toString());
    }

    for (const input of root.querySelectorAll('[data-pg-variant-input]')) {
      /** @type {HTMLInputElement} */ (input).value = variant ? String(variant.id) : '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.emit('pg:variant:change', { variant, selectedOptions });

    const deferred = ProductSelectEvent.createPromise();
    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: { variantId: variant ? String(variant.id) : '' },
        promise: deferred.promise,
      }),
    );

    if (!variant) {
      root.toggleAttribute('data-variant-missing', true);
      deferred.resolve({ variant: null, detail: { productId: this.dataset.productId ?? '' } });
      return;
    }
    root.removeAttribute('data-variant-missing');
    root.setAttribute('data-variant-loading', '');

    try {
      const url = new URL(this.dataset.productUrl || location.pathname, location.origin);
      url.searchParams.set('variant', String(variant.id));
      const html = await fetchSection(this.dataset.sectionId || '', url.toString(), {
        key: `variants-${this.dataset.sectionId}`,
      });
      if (request !== this.#request) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const activeId = document.activeElement?.id;
      for (const part of root.querySelectorAll('[data-pg-variant-part]')) {
        const key = part.getAttribute('data-pg-variant-part') || '';
        const next = doc.querySelector(`[data-pg-variant-part="${CSS.escape(key)}"]`);
        if (next) part.replaceWith(document.importNode(next, true));
      }
      if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
      root.dispatchEvent(new CustomEvent('pg:content:rendered', { bubbles: true }));

      let payload = null;
      try {
        payload = JSON.parse(doc.querySelector('[view-event-payload]')?.getAttribute('view-event-payload') || 'null');
      } catch {
        /* the payload is a nicety for listeners; ignore a malformed one */
      }
      deferred.resolve({
        variant: payload?.product?.selectedVariant ?? null,
        detail: { html: doc, productId: this.dataset.productId ?? '', resource: variant },
      });
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      // The instant updates already happened; the price block just stays as it was.
      deferred.resolve({ variant: null, detail: { didError: true } });
    } finally {
      if (request === this.#request) root.removeAttribute('data-variant-loading');
    }
  }
}

define('pg-variant-picker', PgVariantPicker);
